import React, { ReactNode, useId, useRef, useState } from "react";
import { TextField } from "@mui/material";
import { Box } from "@mui/system";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import uploadImageIcon from "../assets/images/AddNewGigPage/upload_icon@2x.png";
import "./UploadImagesSectionDraggable.scss";
import SnackBar, { SnackTypes } from "../components/UtilityComponents/SnackBar/SnackBar";
import { GigPortFolioImage } from "../pages/AddNewGigPage/ListingDetailsPage/ListingdetailsPage";
import ImageListModal from "../components/CommonComponents/ImageListModal/ImageListModal";
import { GigFormDataInterface } from "../components/PageComponents/SellerDashboardComponents/DashboardSections/GigEditingPage";

interface UploadImagesSectionDraggableProps {
  editMode: boolean;
  gigFormData: GigFormDataInterface;
  updateGigFormData: (fieldName: string, value: any) => void;
}

type DragAndDropProps<T> = {
  items: T[];
  render(item: T, index: number): ReactNode;
  onDragEnd(newItems: T[]): void;
  editMode: Boolean;
};

function DragAndDrop<T>({ items, render, onDragEnd, editMode }: DragAndDropProps<T>) {
  const [temporaryItems, setTemporaryItems] = useState<T[]>();
  const [draggedItem, setDraggedItem] = useState<T>();
  const draggedItemRef = useRef<T>();
  const temporaryItemsRef = useRef<T[]>();

  const renderedItems = temporaryItems || items;

  const finishDrag = () => {
    if (!draggedItemRef.current) return;
    if (temporaryItemsRef.current) onDragEnd(temporaryItemsRef.current);
    draggedItemRef.current = undefined;
    temporaryItemsRef.current = undefined;
    setDraggedItem(undefined);
    setTemporaryItems(undefined);
  };

  return (
    <>
      {renderedItems.map((item, index) => (
        <div
          key={(item as any).assetId ?? (item as any).remoteUrl ?? (item as any).localUrl ?? index}
          style={{ cursor: "grab", ...(item === draggedItem ? { opacity: "0.4", border: "2px dashed grey" } : {}) }}
          draggable={editMode ? true : false}
          onDragStart={() => {
            if(!editMode) return
            draggedItemRef.current = item;
            setDraggedItem(item)
          }}
          onDragOver={(e) => {
            if (!editMode) return 
            e.preventDefault();
            const activeItem = draggedItemRef.current;
            if (!activeItem || activeItem === item) {
              return;
            }
            const currentItems = temporaryItemsRef.current || items;
            const currentIndex = currentItems.indexOf(activeItem);
            const targetIndex = currentItems.indexOf(item);

            if (currentIndex !== -1 && targetIndex !== -1) {
              const newItems = [...currentItems];
              newItems.splice(currentIndex, 1);
              newItems.splice(targetIndex, 0, activeItem);
              temporaryItemsRef.current = newItems;
              setTemporaryItems(newItems);
            }
          }}
          onDrop={(event) => {
            if (!editMode) return;
            event.preventDefault();
            finishDrag();
          }}
          onDragEnd={finishDrag}
        >
          {render(item, index)}
        </div>
      ))}
    </>
  );
}

const UploadImagesSectionDraggable: React.FC<UploadImagesSectionDraggableProps> = ({ editMode, gigFormData, updateGigFormData }) => {
  const fileInputId = useId();

  // Snackbar based status handling 
  const snackBarRef = useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state })
    snackBarRef.current?.showSnackbar()
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const handleOpenModal = (idx: number) => {
    setModalIndex(idx);
    setModalOpen(true);
  };

  const handleFilesAdded = (event: any) => {
    if (event.target.files) {
      if (gigFormData.images.length + event.target.files.length > 10) {
        showSnackbar("A maximum of 10 total portfolio images can be uploaded", SnackTypes.error)
        return;
      }

      let imageList: GigPortFolioImage[] = [...gigFormData.images];
      Array.from(event.target.files).forEach((element: any) => {
        imageList.push({
          localUrl: URL.createObjectURL(element),
          file: element
        });
      });
      
      updateGigFormData('images', imageList);
      updateGigFormData('portfolioUpdated', true)
    }
  }
  
  const handleFileRemoved = (index: number) => {
    updateGigFormData('images', [...gigFormData.images].filter((_, idx) => idx !== index))
    updateGigFormData('portfolioUpdated', true)

    // Add Deletion url to the deletion list
    let imageUrl = gigFormData.images[index].remoteUrl
    if (imageUrl && !gigFormData.images[index].assetId) {
      const imagesToDelete = [...(gigFormData.gigImageUrlsToDelete ?? []), imageUrl]
      updateGigFormData('gigImageUrlsToDelete', imagesToDelete)
    }
  }

  return (
    <div style={{ display: "flex", textAlign: 'center', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
      <DragAndDrop 
        items={gigFormData.images} 
        onDragEnd={(newImages: GigPortFolioImage[]) => {
          updateGigFormData('images', newImages)
          updateGigFormData('portfolioUpdated', true)
        } } 
        render={renderImage} 
        editMode={editMode} 
      />
      <ImageListModal
        images={gigFormData.images.map((img) => img.remoteUrl! ?? img.localUrl)}
        initialIndex={modalIndex}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
      {editMode && (
        <>
          <label htmlFor={fileInputId}>
            <Box
              component="img"
              className="portfolio-image upload-icon"
              src={uploadImageIcon}
              height={{ xs: "100px", md: "150px" }}
              width={{ xs: "100px", md: "150px" }}
              display={gigFormData.images.length >= 10 ? "none" : "block"}
            />
          </label>
          <TextField
            id={fileInputId}
            type="file"
            sx={{ display: "none" }}
            onChange={handleFilesAdded}
            inputProps={{ multiple: true, accept: 'image/png, image/jpg, image/jpeg' }}
          />
          <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />
        </>
      )}
    </div>
  );

  function renderImage(image: GigPortFolioImage, index: number): ReactNode {
    return (
      <div className="image-upload-container">
        <div
          key={index}
          className="image-box"
          style={{ cursor: "grab" }}
        >
          <Box
            component="img"
            className="portfolio-image"
            src={image.remoteUrl ?? image.localUrl}
            height={{ xs: "100px", md: "150px" }}
            width={{ xs: "100px", md: "150px" }}
            onClick={()=>{handleOpenModal(index)}}
            sx={{ cursor: editMode ? 'hand' : 'pointer' }}
          />
          <div>
            {editMode && (
              <div className="cross-button">
                <CancelRoundedIcon
                  className="delete-icon"
                  fontSize="large"
                  color="error"
                  onClick={() => {
                    handleFileRemoved(index);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default UploadImagesSectionDraggable;
